import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, User, Lock, Mail, Shield, UserPlus, Trash2, Edit } from 'lucide-react';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import { adminAPI } from '../../services/api';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    // Get current user from localStorage
    const userJson = localStorage.getItem('admin_user');
    if (userJson) {
      setCurrentUser(JSON.parse(userJson));
    }

    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers();
      if (response.records) {
        setUsers(response.records);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (user: any) => {
    try {
      // Prevent deleting yourself
      if (user.id === currentUser?.id) {
        alert('Vous ne pouvez pas supprimer votre propre compte.');
        return;
      }
      
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.name} ?`)) {
        await adminAPI.deleteUser(user.id);
        setUsers(prev => prev.filter(item => item.id !== user.id));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleSubmitUser = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingUser) {
        // Update existing user
        const updatedData = { ...formData, id: editingUser.id };
        await adminAPI.updateUser(updatedData);
        
        setUsers(prev => prev.map(item => 
          item.id === editingUser.id ? { ...item, ...formData } : item
        ));
      } else {
        // Add new user
        const response = await adminAPI.createUser(formData);
        
        if (response.id) {
          const newUser = {
            id: response.id,
            ...formData,
            created_at: new Date().toISOString()
          };
          setUsers(prev => [...prev, newUser]);
        }
      }
      
      setShowUserModal(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Erreur lors de l\'enregistrement de l\'utilisateur');
    } finally {
      setFormLoading(false);
    }
  };

  const userFormFields = [
    {
      name: 'name',
      label: 'Nom complet',
      type: 'text' as const,
      required: true,
      placeholder: 'Nom de l\'utilisateur'
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email' as const,
      required: true,
      placeholder: 'email@example.com'
    },
    {
      name: 'password',
      label: editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe',
      type: 'text' as const,
      required: !editingUser,
      placeholder: '••••••••'
    },
    {
      name: 'role',
      label: 'Rôle',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'admin', label: 'Administrateur' },
        { value: 'editor', label: 'Éditeur' },
        { value: 'moderator', label: 'Modérateur' }
      ]
    },
    {
      name: 'active',
      label: 'Actif',
      type: 'checkbox' as const
    }
  ];

  const userColumns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value: string, row: any) => (
        <div className="flex items-center">
          <div className="bg-blue-100 text-blue-600 p-2 rounded-full mr-3">
            <User className="h-5 w-5" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Rôle',
      render: (value: string) => {
        const roles: {[key: string]: {label: string, color: string, icon: React.ReactNode}} = {
          'admin': { 
            label: 'Administrateur', 
            color: 'bg-red-100 text-red-800',
            icon: <Shield className="h-3 w-3 mr-1" />
          },
          'editor': { 
            label: 'Éditeur', 
            color: 'bg-blue-100 text-blue-800',
            icon: <Edit className="h-3 w-3 mr-1" />
          },
          'moderator': { 
            label: 'Modérateur', 
            color: 'bg-green-100 text-green-800',
            icon: <User className="h-3 w-3 mr-1" />
          }
        };
        
        const role = roles[value] || { label: value, color: 'bg-gray-100 text-gray-800', icon: null };
        
        return (
          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${role.color}`}>
            {role.icon}
            {role.label}
          </span>
        );
      }
    },
    {
      key: 'active',
      label: 'Statut',
      render: (value: boolean) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Actif' : 'Inactif'}
        </span>
      )
    },
    {
      key: 'last_login',
      label: 'Dernière connexion',
      sortable: true,
      render: (value: string) => value 
        ? new Date(value).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : 'Jamais'
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR')
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* General Settings */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Paramètres généraux</h2>
          
          <form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="site_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du site
                </label>
                <input
                  type="text"
                  id="site_name"
                  name="site_name"
                  defaultValue="ASCsurf - Confédération Africaine de Surf"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email de contact
                </label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  defaultValue="contact@ascsurf.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="default_language" className="block text-sm font-medium text-gray-700 mb-2">
                Langue par défaut
              </label>
              <select
                id="default_language"
                name="default_language"
                defaultValue="fr"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="fr">Français</option>
                <option value="en">Anglais</option>
              </select>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>Enregistrer</span>
              </button>
            </div>
          </form>
        </div>
        
        {/* User Management */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h2>
            <button
              onClick={handleAddUser}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <UserPlus className="h-4 w-4" />
              <span>Nouvel utilisateur</span>
            </button>
          </div>
          
          <AdminTable
            title=""
            data={users}
            columns={userColumns}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            searchable
            filterable={false}
            selectable={false}
            pagination
            itemsPerPage={10}
          />
        </div>
        
        {/* User Modal */}
        <AdminModal
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
          }}
          title={editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          size="md"
        >
          <AdminForm
            fields={userFormFields}
            initialData={editingUser || { active: true, role: 'editor' }}
            onSubmit={handleSubmitUser}
            onCancel={() => {
              setShowUserModal(false);
              setEditingUser(null);
            }}
            loading={formLoading}
          />
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminSettings;